import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { format } from 'date-fns';
import { isGameLive } from './game-utils';

// Pre-game reminder scheduling (on-device local notifications — no backend).
// Everything here is best-effort and no-ops on web: the schedule in the app is
// the source of truth, and the reminder is a glanceable nudge. Called from a
// Dashboard effect whenever the games list or the team setting changes; the
// reconcile is idempotent (cancel all pending, re-schedule the desired set).
//
// Timing rule (Dan, 2026-07-04): fire 8 hours before first pitch, but if that
// lands overnight (9:00pm–7:59am local) pull back to 8:00pm the evening before.
// The overnight guard also covers all-day calendar imports, which carry a
// fabricated noon start time (CalendarImporter) — 8h before noon would be 4am.

const REMINDER_LEAD_MS = 8 * 60 * 60 * 1000;
const MAX_PENDING = 20; // stay well under iOS's 64-pending-notification cap

/** Stable positive int32 id from a Firestore game id (Capacitor ids are numbers). */
function notificationId(gameId: string): number {
  let h = 0;
  for (let i = 0; i < gameId.length; i++) {
    h = (Math.imul(h, 31) + gameId.charCodeAt(i)) | 0;
  }
  return Math.abs(h) || 1;
}

/** 8h before first pitch, with the overnight guard applied. */
function reminderTimeFor(gameDate: Date): { fireAt: Date; eveningBefore: boolean } {
  const fireAt = new Date(gameDate.getTime() - REMINDER_LEAD_MS);
  const hour = fireAt.getHours();
  if (hour >= 21 || hour < 8) {
    // Pull back to 8:00pm on the calendar day before the game.
    const evening = new Date(gameDate);
    evening.setDate(evening.getDate() - 1);
    evening.setHours(20, 0, 0, 0);
    return { fireAt: evening, eveningBefore: true };
  }
  return { fireAt, eveningBefore: false };
}

/**
 * Reconcile pending reminders against the current games list. Cancel-all +
 * re-schedule keeps this immune to drift from edits, deletes, and completions —
 * the games onSnapshot re-fires this on every change.
 */
export async function reconcileGameReminders(games: any[], enabled: boolean): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const pending = await LocalNotifications.getPending();
    if (pending.notifications.length > 0) {
      await LocalNotifications.cancel({ notifications: pending.notifications.map(n => ({ id: n.id })) });
    }
    if (!enabled) return;

    const now = Date.now();
    const desired = (games || [])
      .filter((g: any) => g?.date && g.status !== 'completed' && !isGameLive(g))
      .map((g: any) => {
        const gameDate = new Date(g.date);
        if (isNaN(gameDate.getTime()) || gameDate.getTime() <= now) return null;
        const { fireAt, eveningBefore } = reminderTimeFor(gameDate);
        // A game created inside its reminder window just gets no reminder —
        // the coach literally just touched it.
        if (fireAt.getTime() <= now) return null;
        const matchup = `${g.homeOrAway === 'away' ? 'at' : 'vs'} ${g.opponent || 'opponent'}`;
        const pitch = format(gameDate, 'h:mm a');
        return {
          id: notificationId(g.id),
          title: eveningBefore ? `Tomorrow: ${matchup}` : `Game day: ${matchup}`,
          body: eveningBefore
            ? `First pitch at ${pitch}. Sleep on that lineup.`
            : `First pitch at ${pitch}. Lineup ready, Coach?`,
          schedule: { at: fireAt },
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => a.schedule.at.getTime() - b.schedule.at.getTime())
      .slice(0, MAX_PENDING);

    if (desired.length === 0) return;

    // First schedulable game is the permission moment (Dan's call: default-on,
    // prompt at first schedule). Denial → permanent silent no-op.
    let perm = await LocalNotifications.checkPermissions();
    if (perm.display === 'prompt' || perm.display === 'prompt-with-rationale') {
      perm = await LocalNotifications.requestPermissions();
    }
    if (perm.display !== 'granted') return;

    await LocalNotifications.schedule({ notifications: desired as any });
  } catch {
    /* best-effort — the in-app schedule is the source of truth */
  }
}
